import * as THREE from 'three'
import * as OBC from '@thatopen/components'
import type { ClashBox } from '@viewer/clash/lib'
import { viewerApi } from '@viewer/lib/viewerApi'
import type { Viewpoint } from '@viewer/lib/viewpoint'
import { collectWorldTriangles, type WorldTrianglesSource } from '@viewer/lib/worldTriangles'
import { CLASH_COLOR_A, CLASH_COLOR_B } from './clashCores'
import {
  CLASH_STYLE_A,
  CLASH_STYLE_B,
  CLASH_STYLE_CONTEXTO,
  prepararEstilosDoRealce,
} from './clashHighlight'

// ---------------------------------------------------------------------------
// Imagens dos topicos BCF dos conflitos.
//
// Para cada conflito, sem animacao: poe-se a camara como no viewpoint,
// aplica-se o realce A/B com o resto em fantasma e a caixa de corte do
// par, desenha-se UM frame e le-se a imagem. No fim repoe-se exactamente
// o que o utilizador tinha: camara, fantasma, seleccao (o estilo, o
// store nunca e tocado) e caixa de corte.
//
// Separado em duas partes para se poder testar sem WebGL:
//   - `capturarImagens`: o ciclo (progresso, cancelar, medir, e o
//     `finally` que repoe sempre o estado), sobre um `PalcoDeCaptura`;
//   - `palcoDoViewer`: o palco verdadeiro, sobre o viewer ThatOpen.
//
// Armadilhas tidas em conta:
//   - O rAF nao corre com o painel escondido (aba em segundo plano, 3D
//     tapado pelo modulo). Nada aqui espera por um frame: o desenho e
//     explicito (`renderer.render`) e a leitura e feita na mesma tarefa.
//   - Sem `preserveDrawingBuffer`, o buffer do canvas e descartado depois
//     de composto. Lendo-o (drawImage) na mesma tarefa em que se desenhou,
//     ainda la esta.
//   - O Fragments so desenha os tiles depois de `core.update(true)` com a
//     camara nova: chama-se e espera-se antes de desenhar.
// ---------------------------------------------------------------------------

export interface AlvoDeCaptura {
  modelId: string
  localId: number
}

export interface CenaDeCaptura {
  viewpoint: Viewpoint
  a: AlvoDeCaptura[]
  b: AlvoDeCaptura[]
  /** Caixa de corte (cena) a volta da zona. */
  caixa: ClashBox
}

/** O que a captura precisa do viewer. O verdadeiro e `palcoDoViewer`;
 *  os testes usam um falso. */
export interface PalcoDeCaptura {
  /** Fotografa o estado do utilizador (opaco para quem chama). */
  guardar(): Promise<unknown>
  /** Repoe o que `guardar` fotografou. */
  repor(estado: unknown): Promise<void>
  /** Camara, realce A/B, fantasma e caixa de corte de uma cena. */
  preparar(cena: CenaDeCaptura): Promise<void>
  /** Desenha um frame e devolve o JPEG (ou null se nao conseguir). */
  capturar(maxLargura: number): Promise<Uint8Array | null>
}

export interface ProgressoDaCaptura {
  feitas: number
  total: number
}

export interface ResultadoDaCaptura {
  /** JPEG por cena, pela ordem dada (null: falhou ou foi cancelada). */
  imagens: (Uint8Array | null)[]
  /** Milissegundos de cada imagem feita. */
  msPorImagem: number[]
  cancelado: boolean
}

/** Largura maxima das imagens, em pixeis. */
export const LARGURA_MAXIMA_IMAGEM = 1280
/** Proporcao fixa das imagens: 16:9 (1280 x 720), seja qual for o
 *  canvas do viewer. */
export const ASPECTO_IMAGEM = 16 / 9
/** Qualidade do JPEG. Um PNG por topico dava 157 MB em 138 topicos. */
export const QUALIDADE_JPEG = 0.85

function agora(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

/**
 * Captura uma imagem por cena. Repoe SEMPRE o estado do utilizador no
 * fim, tambem quando se cancela ou quando uma captura rebenta.
 */
export async function capturarImagens(
  cenas: readonly CenaDeCaptura[],
  palco: PalcoDeCaptura,
  opts: {
    signal?: { aborted: boolean }
    onProgress?: (p: ProgressoDaCaptura) => void
    maxLargura?: number
  } = {},
): Promise<ResultadoDaCaptura> {
  const imagens: (Uint8Array | null)[] = cenas.map(() => null)
  const msPorImagem: number[] = []
  let cancelado = false
  const estado = await palco.guardar()
  try {
    for (let i = 0; i < cenas.length; i++) {
      if (opts.signal?.aborted) {
        cancelado = true
        break
      }
      opts.onProgress?.({ feitas: i, total: cenas.length })
      const t0 = agora()
      try {
        await palco.preparar(cenas[i])
        imagens[i] = await palco.capturar(opts.maxLargura ?? LARGURA_MAXIMA_IMAGEM)
      } catch (err) {
        console.warn('[clash] imagem do conflito falhou', err)
        imagens[i] = null
      }
      msPorImagem.push(agora() - t0)
    }
    if (!cancelado) opts.onProgress?.({ feitas: cenas.length, total: cenas.length })
  } finally {
    await palco.repor(estado)
  }
  return { imagens, msPorImagem, cancelado }
}

/** Media de uma lista de tempos, arredondada. */
export function mediaMs(ms: readonly number[]): number {
  if (ms.length === 0) return 0
  return Math.round(ms.reduce((s, x) => s + x, 0) / ms.length)
}

// --- O palco verdadeiro --------------------------------------------------------

type MapaDeIds = Record<string, Set<number>>

interface HighlighterLike {
  selection: Record<string, MapaDeIds | undefined>
  clear: (name?: string) => Promise<void>
  highlightByID: (
    name: string,
    map: MapaDeIds,
    removePrevious?: boolean,
    zoomToSelection?: boolean,
  ) => Promise<void>
}

interface ControlsLike {
  getPosition: (out: THREE.Vector3) => THREE.Vector3
  getTarget: (out: THREE.Vector3) => THREE.Vector3
  setLookAt: (
    px: number, py: number, pz: number,
    tx: number, ty: number, tz: number,
    transition?: boolean,
  ) => Promise<void> | void
  update?: (delta: number) => boolean
}

interface EstadoDoViewer {
  posicao: THREE.Vector3
  alvo: THREE.Vector3
  fov: number | null
  aspecto: number | null
  cortes: THREE.Plane[]
  estilos: Record<string, MapaDeIds>
}

/** Estilos do Highlighter que a captura mexe e repoe. */
const ESTILOS = ['select', 'ghost', CLASH_STYLE_A, CLASH_STYLE_B, CLASH_STYLE_CONTEXTO]

function copiaMapa(m: MapaDeIds | undefined): MapaDeIds {
  const out: MapaDeIds = {}
  for (const [k, v] of Object.entries(m ?? {})) {
    if (v && v.size > 0) out[k] = new Set(v)
  }
  return out
}

function mapaDe(alvos: readonly AlvoDeCaptura[]): MapaDeIds {
  const out: MapaDeIds = {}
  for (const t of alvos) (out[t.modelId] ??= new Set()).add(t.localId)
  return out
}

/** Planos do three que deixam visivel o interior da caixa (o three corta
 *  o lado negativo de cada plano, por isso as normais apontam para
 *  dentro). */
function planosDeCorte(c: ClashBox): THREE.Plane[] {
  const [x0, y0, z0] = c.min
  const [x1, y1, z1] = c.max
  return [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), -x0),
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), x1),
    new THREE.Plane(new THREE.Vector3(0, 1, 0), -y0),
    new THREE.Plane(new THREE.Vector3(0, -1, 0), y1),
    new THREE.Plane(new THREE.Vector3(0, 0, 1), -z0),
    new THREE.Plane(new THREE.Vector3(0, 0, -1), z1),
  ]
}

async function jpegDe(canvas: HTMLCanvasElement): Promise<Uint8Array | null> {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/jpeg', QUALIDADE_JPEG),
  )
  return blob ? new Uint8Array(await blob.arrayBuffer()) : null
}

/** Triangulos maximos do desenho "a ver atraves" de uma cena. */
const TRIANGULOS_SOBREPOSICAO = 300_000

/**
 * O par desenhado POR CIMA de tudo (sem teste de profundidade): um veu
 * da cor do lado e as arestas. Sem isto, um tubo embebido numa parede
 * (o caso tipico de um atravessamento) ficava escondido pela parede e a
 * imagem nao mostrava o conflito. Mesma ideia da "Seleccao atravessa"
 * (useSelecaoAtravessa.ts), so durante o desenho da imagem.
 */
async function sobreposicaoDoPar(cena: CenaDeCaptura): Promise<THREE.Group> {
  const grupo = new THREE.Group()
  grupo.name = 'clash-imagem-sobreposicao'
  const origem = new THREE.Vector3(
    (cena.caixa.min[0] + cena.caixa.max[0]) / 2,
    (cena.caixa.min[1] + cena.caixa.max[1]) / 2,
    (cena.caixa.min[2] + cena.caixa.max[2]) / 2,
  )
  let restantes = TRIANGULOS_SOBREPOSICAO
  const lados: [AlvoDeCaptura[], string, number][] = [
    [cena.a, CLASH_COLOR_A, 0.18],
    [cena.b, CLASH_COLOR_B, 0.45],
  ]
  for (const [alvos, cor, opacidade] of lados) {
    const veu = new THREE.MeshBasicMaterial({
      color: cor,
      transparent: true,
      opacity: opacidade,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    const linhas = new THREE.LineBasicMaterial({
      color: cor,
      transparent: true,
      opacity: 0.95,
      depthTest: false,
    })
    const porModelo = new Map<string, number[]>()
    for (const a of alvos) {
      const l = porModelo.get(a.modelId) ?? []
      if (!l.includes(a.localId)) l.push(a.localId)
      porModelo.set(a.modelId, l)
    }
    for (const [modelId, ids] of porModelo) {
      const modelo = viewerApi.getModel(modelId)
      if (!modelo || restantes <= 0) continue
      try {
        const r = await collectWorldTriangles(modelo as unknown as WorldTrianglesSource, {
          ids,
          maxTriangles: restantes,
          origin: [origem.x, origem.y, origem.z],
        })
        restantes -= r.triangles
        for (const item of r.items) {
          const g = new THREE.BufferGeometry()
          g.setAttribute('position', new THREE.BufferAttribute(item.positions, 3))
          g.setIndex(new THREE.BufferAttribute(item.indices, 1))
          const malha = new THREE.Mesh(g, veu)
          const contorno = new THREE.LineSegments(new THREE.EdgesGeometry(g, 30), linhas)
          malha.renderOrder = 998
          contorno.renderOrder = 999
          grupo.add(malha, contorno)
        }
      } catch (err) {
        console.debug('[clash] sobreposicao do par ignorada', err)
      }
    }
  }
  grupo.position.copy(origem)
  grupo.updateMatrixWorld(true)
  return grupo
}

function largarGrupo(grupo: THREE.Group | null): void {
  if (!grupo) return
  grupo.removeFromParent()
  grupo.traverse((o) => {
    const x = o as THREE.Mesh
    x.geometry?.dispose?.()
    const mats = Array.isArray(x.material) ? x.material : x.material ? [x.material] : []
    for (const m of mats) m.dispose()
  })
}

/** Palco sobre o viewer ThatOpen carregado. Null sem viewer. */
export function palcoDoViewer(): PalcoDeCaptura | null {
  const world = viewerApi.getWorld()
  const hl = viewerApi.getHighlighter() as unknown as HighlighterLike | null
  const renderer = world?.renderer?.three
  const scene = world?.scene?.three
  if (!world || !hl || !renderer || !scene) return null
  const camera = world.camera.three as THREE.PerspectiveCamera | THREE.OrthographicCamera
  const controls = world.camera.controls as unknown as ControlsLike
  const components = (world as unknown as { components?: OBC.Components }).components
  const fragments = components?.get(OBC.FragmentsManager)
  /** A grelha do chao so atrapalha a imagem: sai durante o desenho. */
  const grelhas = (() => {
    try {
      return [...(components?.get(OBC.Grids).list.values() ?? [])]
    } catch {
      return []
    }
  })()
  const aspecto = ASPECTO_IMAGEM
  /** Visiveis por modelo, lidos uma vez (a visibilidade nao muda durante
   *  a captura). */
  let visiveis: Map<string, number[]> | null = null
  /** Planos de corte da cena em preparacao. */
  let cortesDaCena: THREE.Plane[] = []
  /** O par desenhado por cima, da cena em preparacao. */
  let sobreposicao: THREE.Group | null = null

  const actualizarFragments = async (): Promise<void> => {
    try {
      await fragments?.core.update(true)
    } catch (err) {
      console.debug('[clash] update do Fragments ignorado', err)
    }
  }

  const porCamara = (
    pos: THREE.Vector3,
    alvo: THREE.Vector3,
    fov: number | null,
    asp: number | null,
  ): void => {
    if (camera instanceof THREE.PerspectiveCamera) {
      if (fov !== null) camera.fov = fov
      if (asp !== null) camera.aspect = asp
      camera.updateProjectionMatrix()
    }
    void controls.setLookAt(pos.x, pos.y, pos.z, alvo.x, alvo.y, alvo.z, false)
    // Aplica ja a camara (o ciclo do camera-controls corre no rAF, que
    // pode estar parado) e escreve-a tambem directamente na camara do
    // three: o frame que se desenha a seguir tem de ser desta vista,
    // dependa ou nao do ciclo dos controlos.
    controls.update?.(0)
    camera.position.copy(pos)
    camera.up.set(0, 1, 0)
    camera.lookAt(alvo)
    camera.updateMatrixWorld(true)
  }

  return {
    async guardar() {
      const estilos: Record<string, MapaDeIds> = {}
      for (const s of ESTILOS) estilos[s] = copiaMapa(hl.selection[s])
      const estado: EstadoDoViewer = {
        posicao: controls.getPosition(new THREE.Vector3()),
        alvo: controls.getTarget(new THREE.Vector3()),
        fov: camera instanceof THREE.PerspectiveCamera ? camera.fov : null,
        aspecto: camera instanceof THREE.PerspectiveCamera ? camera.aspect : null,
        cortes: [...(renderer.clippingPlanes ?? [])],
        estilos,
      }
      prepararEstilosDoRealce()
      return estado
    },

    async repor(bruto) {
      largarGrupo(sobreposicao)
      sobreposicao = null
      const e = bruto as EstadoDoViewer
      porCamara(e.posicao, e.alvo, e.fov, e.aspecto)
      renderer.clippingPlanes = e.cortes
      for (const s of ESTILOS) {
        const mapa = e.estilos[s] ?? {}
        try {
          if (Object.keys(mapa).length === 0) await hl.clear(s)
          else await hl.highlightByID(s, mapa, true, false)
        } catch (err) {
          console.debug(`[clash] repor estilo ${s} ignorado`, err)
        }
      }
      await actualizarFragments()
      viewerApi._invalidate()
    },

    async preparar(cena) {
      const vp = cena.viewpoint
      const pos = new THREE.Vector3(vp.camera.position.x, vp.camera.position.y, vp.camera.position.z)
      const t = vp.camera.target ?? {
        x: pos.x + vp.camera.direction.x * 10,
        y: pos.y + vp.camera.direction.y * 10,
        z: pos.z + vp.camera.direction.z * 10,
      }
      porCamara(pos, new THREE.Vector3(t.x, t.y, t.z), vp.camera.field_of_view ?? null, aspecto)
      cortesDaCena = planosDeCorte(cena.caixa)
      renderer.clippingPlanes = cortesDaCena

      if (!visiveis) {
        visiveis = new Map()
        for (const [modelId, model] of viewerApi.iterModels()) {
          try {
            visiveis.set(modelId, await model.getItemsByVisibility(true))
          } catch {
            visiveis.set(modelId, [])
          }
        }
      }
      const doPar = new Set([...cena.a, ...cena.b].map((x) => `${x.modelId}:${x.localId}`))
      const fantasma: MapaDeIds = {}
      for (const [modelId, ids] of visiveis) {
        const set = new Set<number>()
        for (const id of ids) if (!doPar.has(`${modelId}:${id}`)) set.add(id)
        if (set.size > 0) fantasma[modelId] = set
      }
      // O estilo `select` ganha a todos os outros no Highlighter, e o
      // fantasma translucido nao sai no desenho explicito: os dois saem
      // durante a captura (voltam no `repor`) e o resto do modelo vai a
      // cinzento opaco (ver CLASH_STYLE_CONTEXTO).
      await hl.clear('select')
      await hl.clear('ghost')
      await hl.highlightByID(CLASH_STYLE_CONTEXTO, fantasma, true, false)
      await hl.highlightByID(CLASH_STYLE_A, mapaDe(cena.a), true, false)
      await hl.highlightByID(CLASH_STYLE_B, mapaDe(cena.b), true, false)
      largarGrupo(sobreposicao)
      sobreposicao = await sobreposicaoDoPar(cena)
      scene.add(sobreposicao)
      await actualizarFragments()
    },

    async capturar(maxLargura) {
      const largura = Math.max(320, Math.min(maxLargura, 4096))
      const altura = Math.round(largura / aspecto)
      const pr = renderer.getPixelRatio()
      const tamanho = renderer.getSize(new THREE.Vector2())
      const saida = document.createElement('canvas')
      saida.width = largura
      saida.height = altura
      const ctx = saida.getContext('2d')
      if (!ctx) return null
      try {
        // Desenha no tamanho da imagem e le na MESMA tarefa: sem esperar
        // por frame nenhum, e com o buffer ainda la sem
        // preserveDrawingBuffer.
        renderer.setPixelRatio(1)
        renderer.setSize(largura, altura, false)
        // Os cortes e a camara da cena, escritos de novo imediatamente
        // antes do desenho (nada entre o `preparar` e aqui os pode ter
        // trocado sem se dar por isso).
        renderer.clippingPlanes = cortesDaCena
        camera.updateMatrixWorld(true)
        const grelhaVisivel = grelhas.map((g) => g.visible)
        for (const g of grelhas) g.visible = false
        try {
          renderer.render(scene, camera)
          ctx.drawImage(renderer.domElement, 0, 0, largura, altura)
        } finally {
          grelhas.forEach((g, i) => {
            g.visible = grelhaVisivel[i]
          })
        }
      } finally {
        renderer.setPixelRatio(pr)
        renderer.setSize(tamanho.x, tamanho.y, false)
      }
      return jpegDe(saida)
    },
  }
}
